// REST API клиент для сервера «Корпы».
// Базовый путь /api — Vite-прокси перенаправит на localhost:3000 в dev,
// на production это будет тот же домен (nginx/Cloudflare прокси).
//
// JWT хранится в localStorage под ключом 'corps.token'. Автоматически
// прикрепляется к каждому запросу как Authorization: Bearer <token>.

const LS_TOKEN_KEY = 'corps.token';

export function getToken(): string | null {
  return localStorage.getItem(LS_TOKEN_KEY);
}
export function setToken(token: string | null) {
  if (token) localStorage.setItem(LS_TOKEN_KEY, token);
  else localStorage.removeItem(LS_TOKEN_KEY);
}
export function clearToken() { setToken(null); }

class ApiError extends Error {
  constructor(public status: number, public payload: any) {
    super(`API ${status}: ${JSON.stringify(payload)}`);
  }
}

// API_BASE — в dev пусто (Vite-прокси на :3000), в production = VITE_API_URL.
// Пример VITE_API_URL: https://158-160-199-84.nip.io
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const API_BASE = ((import.meta as any).env?.VITE_API_URL || '').replace(/\/$/, '');

export async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string> | undefined)
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const resp = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new ApiError(resp.status, data);
  }
  return data as T;
}

// === Auth ===
export interface AuthResponse {
  ok: boolean;
  token: string;
  // РЕФ.1: для onboarding-модалки «Введи Сигнал».
  isNewPlayer?: boolean;
  hasReferrer?: boolean;
  player: { id: number; nick: string; heroId: string; faction: string; scripts: number; league: string; mmr: number };
}
export function setReferrer(code: string): Promise<{ ok: true; referrerId: number }> {
  return request('/api/profile/set-referrer', {
    method: 'POST',
    body: JSON.stringify({ code })
  });
}
export async function authViaTelegram(initData: string, ref?: string): Promise<AuthResponse> {
  const body: any = { initData };
  if (ref) body.ref = ref;
  const resp = await request<AuthResponse>('/api/auth/telegram', {
    method: 'POST',
    body: JSON.stringify(body)
  });
  // Сохраняем токен в localStorage — переживёт перезагрузку.
  setToken(resp.token);
  return resp;
}

// === Profile ===
export interface PlayerTitleDto {
  id: number;
  title_code: string;
  title_text_ru: string;
  source: string;
}
export interface PlayerCosmeticDto {
  id: number;
  cosmetic_id: string;
  cosmetic_type: string;
  is_equipped: boolean;
  source: string;
}
export interface ProfileResponse {
  player: {
    id: number;
    nick: string;
    heroId: string;
    faction: 'deus' | 'syntetics' | 'global' | 'livlife' | 'neutral';
    scripts: number;
    usdt: number;
    tonAddress: string | null;
    hasBp: boolean;
    bpLevel: number;
    bpXp: number;
    league: string;
    mmr: number;
    // Этап 70: место по MMR среди всех игроков (1 = лучший). null если
    // сервер старый / не посчитал.
    ratingPlace?: number | null;
    winStreak: number;
    // ЛИГ.2: per-faction win streak (4 счётчика — счётчики серий побед
    // подряд по каждой корпе). Используется для ачивок streak_5_<faction>.
    winStreakFactions?: {
      deus: number;
      syntetics: number;
      global: number;
      livlife: number;
    };
    firewalls: number;
    unreadMessages: number;
    regDate: string;
    referralCode: string;
    deletionScheduledAt: string | null;
    // ДРУГ.1
    needsNickSetup?: boolean;
    friendsLimit?: number;
    notificationSettings?: Record<string, boolean>;
    // БП.1
    profileAccentColor?: string | null;
    activeTitle?: { id: number; title_code: string; title_text_ru: string } | null;
    deckSlotsLimit?: number;
    bpXpLifetime?: number;
    // АВ.1
    activeAvatarId?: string | null;
    // ФТУЕ.0
    locale?: 'ru' | 'en';
    // ФТУЕ.1
    ftueStatus?: 'pending' | 'in_progress' | 'skipped' | 'completed';
    ftueStep?: number;
    // ФТУЕ.6
    ftueTooltipsSeen?: string[];
    // ЗВЁЗДЫ.2 (Этап 76): непоказанные изъятия карт за Stars-рефанд.
    pendingStarsClawback?: Array<{
      stars: number; at: string;
      cards: Array<{ id: string; name_ru: string; rarity: string; faction: string }>;
    }>;
    // УР.1
    accountLevel?: number;
    accountXp?: number;
    accountXpInLevel?: number;
    accountXpToNext?: number;
    nextReward?: {
      level: number;
      description: string;
      raw: { scripts?: number; container?: { type: string; count: number };
             title?: { code: string; text: string }; avatar?: string };
    } | null;
    currentLevelReward?: { description: string } | null;
  };
  heroesOwned: string[];
  shifrokontainers: { id: number; type: string; source: string }[];
  decks: any[];
  // БП.1
  titles?: PlayerTitleDto[];
  cosmetics?: PlayerCosmeticDto[];
}
export function getProfile(): Promise<ProfileResponse> {
  return request<ProfileResponse>('/api/profile/me');
}

export function updateProfile(patch: { nick?: string; heroId?: string; locale?: 'ru' | 'en' }): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/profile/me', {
    method: 'PATCH',
    body: JSON.stringify(patch)
  });
}

// ФТУЕ.1: смена состояния обучения.
//   advance — сохранить текущий шаг и пометить 'in_progress'
//   skip    — игрок выбрал «Нет», статус 'skipped'
//   complete — игрок прошёл целиком, статус 'completed' (бонус — на ФТУЕ.5)
//   restart — сбросить ftue_status='pending', step=0 (для перезапуска)
export type FtueAction = 'advance' | 'skip' | 'complete' | 'restart';
export function setFtueState(action: FtueAction, step?: number): Promise<{
  ok: true;
  ftueStatus: 'pending' | 'in_progress' | 'skipped' | 'completed';
  ftueStep: number | null;
}> {
  return request('/api/profile/me/ftue', {
    method: 'POST',
    body: JSON.stringify({ action, ...(typeof step === 'number' ? { step } : {}) })
  });
}

// ФТУЕ.6: отметить tooltip-флоу как пройденный.
// При повторном входе на тот же экран флоу не запустится.
export function markFtueTooltipSeen(code: string): Promise<{
  ok: true;
  list: string[];
  alreadySeen: boolean;
}> {
  return request('/api/profile/me/ftue/tooltip', {
    method: 'POST',
    body: JSON.stringify({ code })
  });
}

// Запросить удаление аккаунта (с задержкой 7 дней).
export function requestAccountDeletion(): Promise<{ ok: true; deletionScheduledAt: string }> {
  return request('/api/profile/me/request-deletion', { method: 'POST' });
}

// МАГ.3: инвентарь карт игрока для фильтра в редакторе колоды.
export interface OwnedCardItem { cardId: string; count: number }
export function getMyCards(): Promise<{ items: OwnedCardItem[] }> {
  return request<{ items: OwnedCardItem[] }>('/api/profile/me/cards');
}

// МСГ: серверный changelog (CF Pages делает shallow-clone, поэтому
// клиентский __GIT_LOG__ показывает только 1 коммит на проде).
export interface ChangelogCommit { hash: string; subject: string; iso_date: string }
export function getDevChangelog(limit = 50): Promise<{ items: ChangelogCommit[] }> {
  return request<{ items: ChangelogCommit[] }>(`/api/dev-changelog?limit=${limit}`);
}

// МАГ.3: серверные эндпоинты покупки и открытия паков.
export type PackTypeApi = 'main_common' | 'main_faction' | 'advanced_common' | 'advanced_faction';
export type FactionApi = 'deus' | 'syntetics' | 'global' | 'livlife';
export type RarityApi = 'common' | 'rare' | 'epic' | 'legendary';

export interface BuyPackResponse {
  ok: true;
  container_id: number;
  type: string;
}
export function buyPackApi(
  packType: PackTypeApi,
  currency: 'usdt' | 'scripts',
  faction?: FactionApi
): Promise<BuyPackResponse> {
  return request<BuyPackResponse>('/api/shop/buy-pack', {
    method: 'POST',
    body: JSON.stringify({ pack_type: packType, currency, ...(faction ? { faction } : {}) })
  });
}

// Этап 74: серверная покупка Героя (раньше — клиентская заглушка).
export function buyHeroApi(heroId: string, currency: 'usdt' | 'scripts'): Promise<{ ok: true; hero_id: string }> {
  return request('/api/shop/buy-hero', {
    method: 'POST',
    body: JSON.stringify({ hero_id: heroId, currency })
  });
}

// ЗВЁЗДЫ.1 (Этап 75): invoice-ссылка на оплату Telegram Stars.
// Открывается через WebApp.openInvoice; выдача товара — после оплаты,
// сервер ловит successful_payment от Telegram.
export function createStarsInvoice(body: {
  kind: 'pack' | 'hero';
  pack_type?: PackTypeApi;
  faction?: FactionApi;
  hero_id?: string;
  label?: string;
}): Promise<{ ok: true; invoice_link: string; stars: number }> {
  return request('/api/stars/create-invoice', {
    method: 'POST',
    body: JSON.stringify(body)
  });
}

// ЗВЁЗДЫ.2 (Этап 76): клиент показал рулетку изъятия карт — снять флаг.
export function markClawbackSeen(): Promise<{ ok: true }> {
  return request('/api/stars/clawback-seen', { method: 'POST' });
}

export interface OpenedCard {
  id: string;
  rarity: RarityApi;
  name_ru: string;
  faction: FactionApi | 'neutral';
}
export interface OpenPackResponse {
  ok: true;
  cards: OpenedCard[];
  pity_triggered: { epic: boolean; leg: boolean };
  pity_after: { epic: number; leg: number };
}
// faction передаётся ТОЛЬКО для legacy-контейнеров с типом 'main_faction' /
// 'advanced_faction' (фракция в имени не зашита). Для остальных — undefined.
export function openPackApi(containerId: number, faction?: FactionApi): Promise<OpenPackResponse> {
  const body = faction ? { faction } : undefined;
  return request<OpenPackResponse>(`/api/shop/open-pack/${containerId}`, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined
  });
}

export interface PityStatusResponse {
  counters: Record<string, { epic: number; leg: number }>;
}
export function getPityStatus(): Promise<PityStatusResponse> {
  return request<PityStatusResponse>('/api/shop/pity-status');
}

// МАГ.1: статус и покупка файерволов (учёт за сезон БП).
export interface FirewallStatus {
  bought: number;
  max: number;
  next: { tier: number; price: number } | null;
  season_ends_at: string | null;
  exhausted_message: string | null;
}
export function getFirewallStatus(): Promise<FirewallStatus> {
  return request<FirewallStatus>('/api/shop/firewall-status');
}
export function buyFirewallApi(): Promise<FirewallStatus & { ok: true; tier: number }> {
  return request('/api/shop/buy-firewall', { method: 'POST' });
}

// РЕФ.2: реальный список приглашённых игроков (тех, у кого referrer_id = я).
export interface ReferralItem {
  id: number;
  nick: string;
  faction: 'deus' | 'syntetics' | 'global' | 'livlife' | 'neutral';
  account_level: number;
  league: string;
  created_at: string;
  last_seen_at: string | null;
  is_active: boolean;
}
export interface MyReferralsResponse {
  total: number;
  active: number;
  list: ReferralItem[];
}
export function listMyReferrals(): Promise<MyReferralsResponse> {
  return request<MyReferralsResponse>('/api/profile/me/referrals');
}
// Отменить запланированное удаление.
export function cancelAccountDeletion(): Promise<{ ok: true }> {
  return request('/api/profile/me/cancel-deletion', { method: 'POST' });
}

// === Decks ===
export interface DeckDto {
  id: number;
  name: string;
  heroId: string;
  faction: 'deus' | 'syntetics' | 'global' | 'livlife' | 'neutral';
  cards: { cardId: string; count: number }[];
  isFavorite: boolean;
  updatedAt: string;
  // КОЛ.1: винрейт по матчам этой колоды.
  // matches=0 → winrate=null (на UI показываем "—").
  wins?: number;
  losses?: number;
  matches?: number;
  winrate?: number | null;
}
export function listDecks(): Promise<DeckDto[]> {
  return request<DeckDto[]>('/api/decks');
}
export function createDeck(data: { name: string; heroId: string; faction: string; cards: { cardId: string; count: number }[] }): Promise<{ id: number }> {
  return request<{ id: number }>('/api/decks', { method: 'POST', body: JSON.stringify(data) });
}
export function updateDeck(id: number, patch: { name?: string; cards?: { cardId: string; count: number }[]; isFavorite?: boolean }): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/decks/${id}`, { method: 'PUT', body: JSON.stringify(patch) });
}
export function deleteDeck(id: number): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/decks/${id}`, { method: 'DELETE' });
}

// === Messages (от админа + авто-уведомления о действиях) ===
// ДРУГ.1: actions — массив кнопок для интерактивных сообщений (например
// запросы в чумбы). Если actionTakenAt не null — кнопки уже скрыты,
// сообщение в архивном виде.
export interface MessageAction {
  label: string;
  type: 'friend_accept' | 'friend_decline_temp' | 'friend_decline_forever' | string;
  payload: Record<string, any>;
}
export interface ServerMessage {
  id: number;
  kind: 'admin' | 'auto';
  category: string | null;
  title: string;
  body: string;
  actions?: MessageAction[] | null;
  actionTakenAt?: string | null;
  readAt: string | null;
  createdAt: string;
}
export interface MessagesResponse {
  messages: ServerMessage[];
  unread: number;
}
export function getMessages(): Promise<MessagesResponse> {
  return request<MessagesResponse>('/api/messages');
}
export function markMessagesRead(): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/messages/mark-read', { method: 'POST' });
}
export function deleteMessage(id: number): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/messages/${id}`, { method: 'DELETE' });
}
export function deleteAllMessages(): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/messages', { method: 'DELETE' });
}

// === ДРУГ.1: друзья («чумбы») ===
export interface PlayerSearchItem {
  id: number;
  nick: string;
  heroId: string;
  faction: string;
  activeAvatarId: string | null;
}
export function searchPlayers(q: string): Promise<{ items: PlayerSearchItem[] }> {
  return request<{ items: PlayerSearchItem[] }>(`/api/players/search?q=${encodeURIComponent(q)}`);
}

// Публичный профиль другого игрока (тапом по чумбе).
export interface PublicProfileResponse {
  player: {
    id: number; nick: string; faction: string; heroId: string;
    league: string; mmr: number; accountLevel: number;
    activeAvatarId: string | null;
    activeTitle: { code: string; text: string } | null;
    profileAccentColor: string | null;
    regDate: string; lastSeenAt: string | null;
  };
  stats: {
    total: number; wins: number; winrate: number;
    byFaction: { faction: string; count: number; winrate: number }[];
  };
  achievements: { code: string; title: string; description: string;
    category: string; tier: string | null; completedAt: string }[];
  isFriend: boolean;
  isSelf: boolean;
}
export function getPublicProfile(playerId: number): Promise<PublicProfileResponse> {
  return request<PublicProfileResponse>(`/api/players/${playerId}/public`);
}

export interface FriendItem {
  id: number;
  nick: string;
  heroId: string;
  faction: string;
  activeAvatarId: string | null;
  since: string;
}
export function getFriends(): Promise<{ items: FriendItem[]; limit: number }> {
  return request<{ items: FriendItem[]; limit: number }>('/api/friends');
}

export interface IncomingRequestItem {
  requestId: number;
  id: number; nick: string; heroId: string; faction: string;
  activeAvatarId: string | null; sentAt: string;
}
export function getIncomingFriendRequests(): Promise<{ items: IncomingRequestItem[] }> {
  return request<{ items: IncomingRequestItem[] }>('/api/friends/incoming');
}

export interface SentRequestItem {
  requestId: number; status: string;
  id: number; nick: string; heroId: string; faction: string;
  activeAvatarId: string | null; sentAt: string; respondedAt: string | null;
}
export function getSentFriendRequests(): Promise<{ items: SentRequestItem[] }> {
  return request<{ items: SentRequestItem[] }>('/api/friends/sent');
}

export interface BlockedItem {
  requestId: number; id: number; nick: string; blockedAt: string;
}
export function getBlocked(): Promise<{ items: BlockedItem[] }> {
  return request<{ items: BlockedItem[] }>('/api/friends/blocked');
}

export function requestFriend(toPlayerId: number): Promise<{ ok: true; requestId?: number; silent?: boolean }> {
  return request('/api/friends/request', {
    method: 'POST',
    body: JSON.stringify({ toPlayerId })
  });
}

export type FriendRespondAction = 'accept' | 'decline_temp' | 'decline_forever';
export function respondFriendRequest(requestId: number, action: FriendRespondAction): Promise<{ ok: true; status: string }> {
  return request('/api/friends/respond', {
    method: 'POST',
    body: JSON.stringify({ requestId, action })
  });
}

export function removeFriend(friendId: number): Promise<{ ok: true }> {
  return request(`/api/friends/${friendId}`, { method: 'DELETE' });
}

// === Quests ===
export type QuestType = 'event' | 'click' | 'external' | 'subscribe';
export type QuestCategory = 'daily' | 'weekly' | 'partner' | 'one_time';

export interface QuestDto {
  id: number;
  title: string;
  description: string | null;
  // ФТУЕ.7: EN-версии полей. Если null — клиент с EN-локалью использует RU.
  titleEn: string | null;
  descriptionEn: string | null;
  type: QuestType;
  eventName: string | null;
  targetValue: number;
  eventFilter: string | null;
  targetUrl: string | null;
  targetChat: string | null;
  category: QuestCategory;
  rewardScripts: number;
  rewardPacks: number;
  rewardPackType: string | null;
  progress: number;
  completed: boolean;
  claimed: boolean;
}

export function listQuests(): Promise<QuestDto[]> {
  return request<QuestDto[]>('/api/quests');
}
export function clickQuest(id: number): Promise<{ ok: true; completed: boolean }> {
  return request(`/api/quests/${id}/click`, { method: 'POST' });
}
export function verifyQuest(id: number): Promise<{ ok: true; completed: boolean }> {
  return request(`/api/quests/${id}/verify`, { method: 'POST' });
}
export function claimQuest(id: number): Promise<{ ok: true; rewardScripts: number; rewardPacks: number; rewardPackType: string | null }> {
  return request(`/api/quests/${id}/claim`, { method: 'POST' });
}

// === Transactions ===
export interface TransactionDto {
  id: number;
  kind: 'scripts' | 'usdt' | 'pack' | 'firewall' | 'hero' | 'bp' | 'card';
  delta: number;     // знаковое; usdt в центах
  reason: string;    // 'quest_reward' / 'admin_grant' / 'shop_pack' / ...
  meta: any;
  createdAt: string;
}
export function listTransactions(kind?: string, limit = 50): Promise<TransactionDto[]> {
  const params = new URLSearchParams();
  if (kind) params.set('kind', kind);
  params.set('limit', String(limit));
  return request<TransactionDto[]>(`/api/transactions?${params.toString()}`);
}

// === ИКЛ.1: USDT custodial wallet ===
export interface WalletInfoResponse {
  balance: {
    total_cents: number;
    locked_cents: number;
    available_cents: number;
  };
  deposit: {
    to_address: string;
    jetton_master: string;
    memo: string;
    notes: string[];
  };
  withdrawal: {
    min_cents: number;
    fee_cents: number;
    max_per_request_cents: number;
    max_daily_cents: number;
    spent_24h_cents: number;
    available_today_cents: number;
    frozen: boolean;
    frozen_reason: string | null;
  };
}
export interface WithdrawalItem {
  id: number;
  to_address: string;
  amount_cents: number;
  fee_cents: number;
  status: 'pending' | 'processing' | 'sent' | 'failed' | 'cancelled';
  tx_hash: string | null;
  failure_reason: string | null;
  createdAt: string;          // ISO
  processed_at: string | null;
}

export function getWalletInfo(): Promise<WalletInfoResponse> {
  return request<WalletInfoResponse>('/api/wallet/info');
}
export function listWithdrawals(): Promise<{ items: WithdrawalItem[] }> {
  // Маппим snake_case created_at → createdAt для единообразия с другими DTO.
  return request<{ items: any[] }>('/api/wallet/withdrawals').then(r => ({
    items: r.items.map(i => ({ ...i, createdAt: i.created_at }))
  }));
}
export function requestWithdraw(toAddress: string, amountCents: number): Promise<{ ok: true; withdrawal: { id: number; amount_cents: number; fee_cents: number; status: string } }> {
  return request('/api/wallet/withdraw', {
    method: 'POST',
    body: JSON.stringify({ toAddress, amountCents })
  });
}

// Готовая TonConnect-транзакция для пополнения через подключённый кошелёк.
export interface DepositTxResponse {
  validUntil: number;
  messages: { address: string; amount: string; payload: string }[];
}
export function buildDepositTx(userAddress: string, amountCents: number): Promise<DepositTxResponse> {
  return request<DepositTxResponse>('/api/wallet/build-deposit-tx', {
    method: 'POST',
    body: JSON.stringify({ userAddress, amountCents })
  });
}

// === Healthcheck (для smoke-теста) ===
export function getHealth(): Promise<{ ok: boolean; timestamp: string; env: string }> {
  return request<{ ok: boolean; timestamp: string; env: string }>('/health');
}

// === БП.1: Battle Pass ===
export type BPTrack = 'free' | 'paid';
export type BPRewardType = 'scripts' | 'container' | 'cosmetic' | 'title' | 'perk_unlock';

export interface BPSeason {
  id: number;
  code: string;
  name: string;
  starts_at: string;
  ends_at: string;
}
export interface BPProgress {
  xp: number;
  level: number;
  xp_in_level: number;
  xp_to_next: number;
  has_paid: boolean;
  paid_purchased_at: string | null;
}
export interface BPCatalogItem {
  level: number;
  track: BPTrack;
  reward_type: BPRewardType;
  payload: any;
  order_in_level: number;
}
export interface BPClaim {
  level: number;
  track: BPTrack;
}
export interface BPActiveTitle {
  id: number;
  title_code: string;
  title_text_ru: string;
}
export interface BattlePassResponse {
  season: BPSeason | null;
  progress: BPProgress | null;
  catalog: BPCatalogItem[];
  claimed: BPClaim[];
  active_title: BPActiveTitle | null;
  unlocked_perks: string[];
  lore_group_invite_url: string | null;
}

export function getBattlePass(): Promise<BattlePassResponse> {
  return request<BattlePassResponse>('/api/battle-pass/current');
}

export function claimBattlePass(level: number, track: BPTrack): Promise<{ ok: true; rewards: string[] }> {
  return request('/api/battle-pass/claim', {
    method: 'POST',
    body: JSON.stringify({ level, track })
  });
}

export function purchaseBattlePass(): Promise<{ ok: true; payment_id: number }> {
  return request('/api/battle-pass/purchase', { method: 'POST' });
}

// БП.1: Profile extensions
export function setActiveTitle(titleId: number | null): Promise<{ ok: true }> {
  return request('/api/profile/active-title', {
    method: 'POST',
    body: JSON.stringify({ title_id: titleId })
  });
}

export function setAccentColor(color: string | null): Promise<{ ok: true }> {
  return request('/api/profile/accent-color', {
    method: 'POST',
    body: JSON.stringify({ color })
  });
}

// АВ.1: выбрать активный пользовательский аватар (из инвентаря).
export function setActiveAvatar(avatarId: string | null): Promise<{ ok: true }> {
  return request('/api/profile/avatar', {
    method: 'POST',
    body: JSON.stringify({ avatar_id: avatarId })
  });
}

// БП.1: Leaderboard (с display_name/accent_color/active_title)
export interface LeaderboardEntry {
  place: number;
  id: number;
  nick: string;
  display_name: string;
  faction: string;
  league: string;
  mmr: number;
  profile_accent_color: string | null;
  active_title: { title_code: string; title_text_ru: string } | null;
}
export function getLeaderboard(limit = 50): Promise<LeaderboardEntry[]> {
  return request<LeaderboardEntry[]>(`/api/leaderboard?limit=${limit}`);
}

// ЛИГ.1.4: запись лидерборда Высшей лиги (формула N = wins - losses).
export interface BigLeagueEntry extends LeaderboardEntry {
  wins: number;
  losses: number;
  score: number; // wins - losses
}
export function getBigLeagueLeaderboard(limit = 50): Promise<BigLeagueEntry[]> {
  return request<BigLeagueEntry[]>(`/api/leaderboard/big-league?limit=${limit}`);
}

// Этап 70: топ Войны Корпораций (по сумме USDT-выигрышей, серверные
// transactions — клиентским цифрам не доверяем).
export interface CorpWarsEntry {
  place: number;
  id: number;
  nick: string;
  display_name: string;
  faction: string;
  profile_accent_color: string | null;
  active_title: { title_code: string; title_text_ru: string } | null;
  wars: number;            // всего войн (победы + поражения)
  won_usdt_cents: number;  // сумма выигрышей в центах
}
export function getCorpWarsLeaderboard(limit = 50): Promise<CorpWarsEntry[]> {
  return request<CorpWarsEntry[]>(`/api/leaderboard/corp-wars?limit=${limit}`);
}

// === ДО.1: Achievements ===
export interface AchievementDto {
  id: number;
  code: string;
  title_ru: string;
  title_en: string;
  description_ru: string;
  description_en: string;
  category: 'combat' | 'collection' | 'social' | 'season' | 'misc';
  tier: 'bronze' | 'silver' | 'gold' | null;
  target_value: number;
  reward_payload: any;
  progress: number;
  completed: boolean;
  claimed: boolean;
  completed_at: string | null;
  claimed_at: string | null;
}
export function listAchievements(): Promise<AchievementDto[]> {
  return request<AchievementDto[]>('/api/achievements');
}
// БУГ.11: claim ачивки. Если reward требует выбора фракции
// (requires_faction_choice — например streak_5_global → ШК главной фракции),
// сервер вернёт { ok: true, requires_faction_choice: true } БЕЗ выдачи.
// Клиент показывает picker и шлёт повторный запрос с faction.
export type AchievementFaction = 'deus' | 'syntetics' | 'global' | 'livlife';
export interface ClaimAchievementResult {
  ok: true;
  rewards?: string;
  requires_faction_choice?: boolean;
}
export function claimAchievement(
  id: number,
  faction?: AchievementFaction
): Promise<ClaimAchievementResult> {
  return request(`/api/achievements/${id}/claim`, {
    method: 'POST',
    body: JSON.stringify(faction ? { faction } : {})
  });
}

// === ЕЖВ.1: Daily Login ===
export interface DailyLoginStatus {
  current_day: number;            // 1..7
  claimed_today: boolean;
  streak_alive: boolean;
  rewards: number[];              // [3,4,5,6,7,8,15]
  total_claims: number;
  longest_streak: number;
  current_streak_length: number;
}
export function getDailyLoginStatus(): Promise<DailyLoginStatus> {
  return request<DailyLoginStatus>('/api/daily-login/status');
}
export function claimDailyLogin(): Promise<{
  ok: true; reward: number; day_claimed: number;
  new_current_day: number; current_streak_length: number;
}> {
  return request('/api/daily-login/claim', { method: 'POST' });
}

// === ПУШ.1: настройки уведомлений ===
export type NotificationCategory =
  | 'support_reply' | 'support_closed' | 'level_up' | 'achievement'
  | 'daily_login_reminder' | 'admin_grant' | 'friend_request' | 'market' | 'system';
export type NotificationSettings = Partial<Record<NotificationCategory, boolean>>;

export function getNotificationSettings(): Promise<{ settings: NotificationSettings }> {
  return request('/api/profile/notifications');
}
export function patchNotificationSettings(settings: NotificationSettings): Promise<{ ok: true; settings: NotificationSettings }> {
  return request('/api/profile/notifications', {
    method: 'PATCH',
    body: JSON.stringify({ settings })
  });
}

// === СУП.1: Support ===
export type SupportTopic = 'bug' | 'payment' | 'question' | 'feedback' | 'other';
export type SupportStatus = 'open' | 'in_progress' | 'in_waiting' | 'closed';

export interface SupportTopicDto {
  code: SupportTopic;
  label: string;
}

export interface SupportTicketSummary {
  id: number;
  code: string;
  topic: SupportTopic;
  subject: string;
  status: SupportStatus;
  // tag намеренно НЕ возвращается сервером юзеру (внутреннее поле саппортера).
  priority: boolean;
  ticket_number: number;
  created_at: string;
  last_response_at: string;
  closed_at: string | null;
}

export interface SupportMessageDto {
  id: number;
  sender: 'user' | 'admin';
  body: string;
  created_at: string;
}

export interface SupportTicketDetail extends SupportTicketSummary {
  messages: SupportMessageDto[];
}

export function getSupportTopics(): Promise<SupportTopicDto[]> {
  return request<SupportTopicDto[]>('/api/support/topics');
}

export function listSupportTickets(): Promise<SupportTicketSummary[]> {
  return request<SupportTicketSummary[]>('/api/support/tickets');
}

export function getSupportTicket(id: number): Promise<SupportTicketDetail> {
  return request<SupportTicketDetail>(`/api/support/tickets/${id}`);
}

export function createSupportTicket(data: {
  topic: SupportTopic; subject?: string; body: string;
}): Promise<{ ok: true; ticket_id: number; code: string; priority: boolean }> {
  return request('/api/support/tickets', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export function postSupportMessage(ticketId: number, body: string): Promise<{ ok: true }> {
  return request(`/api/support/tickets/${ticketId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ body })
  });
}

// ────────── Матчи: финиш + награды (БОЙ.1.6) ──────────

export type MatchOutcomeCode =
  | 'rated_win' | 'rated_loss'
  | 'casual_win' | 'casual_loss'
  | 'war_win' | 'war_loss'
  | 'pve_first' | 'pve_repeat';

// ЛИГ.1.x: что произошло с лигой по итогам рейтингового матча.
export interface MatchLeagueOutcome {
  league: string;        // финальная лига после всех манипуляций
  winStreak: number;
  lossStreak: number;
  firewalls: number;
  promoted?: { from: string; to: string };
  demoted?:  { from: string; to: string };
  firewallGained?: boolean;
  firewallUsedAntiDemote?: boolean;
  firewallUsedAntiYoyo?: boolean;
}

// ЛИГ.1.2: что выдано игроку за подъём лиги (разово в сезон).
export interface LeaguePromotionReward {
  league: string;
  alreadyClaimed: boolean;
  scripts?: number;
  packs?: Array<{ type: string; count: number }>;
  seasonalCardCover?: boolean;
  description: string; // ru-описание для UI
}

export interface MatchFinishResult {
  ok: true;
  match_id: number;
  bp: { gained: number; level: number; xp: number } | null;
  account: {
    xp_delta: number;
    level_ups: number[];
    rewards: string[];
  };
  // Только для рейтинговых матчей. null для casual/war/pve.
  league: MatchLeagueOutcome | null;
  // Только при подъёме лиги (диздок §3.6). null если подъёма не было
  // или награда уже была получена в этом сезоне.
  leaguePromotionReward: LeaguePromotionReward | null;
}

export interface MatchHistoryItem {
  id: number;
  mode: 'pvp_rating' | 'pvp_casual' | 'corp_war' | 'pve' | string;
  faction: string;
  heroId: string;
  result: 'win' | 'loss' | 'draw';
  durationSec: number;
  mmrDelta: number;
  scriptsEarned: number;
  // ВОЙ.6: USDT cents earned in this match. Positive for war wins.
  usdtCentsEarned: number;
  createdAt: string;
  opponent: {
    id: number;
    nick: string;
    faction: string;
    display_name?: string;
    accent_color?: string | null;
    title?: { title_code: string; title_text_ru: string } | null;
  } | null;
}

/** РЕП.1: список последних матчей игрока. Используется ArchiveScreen. */
export function getMatchHistory(limit = 50): Promise<MatchHistoryItem[]> {
  return request<MatchHistoryItem[]>(`/api/matches/history?limit=${limit}`);
}

export function finishMatch(data: {
  outcome: MatchOutcomeCode;
  opponentId?: number | null;
  faction?: string;
  // РЕП.2: фракция соперника в момент матча — для архива.
  opponentFaction?: string;
  heroId?: string;
  durationSec?: number;
  mmrDelta?: number;
  scriptsEarned?: number;
  usdtCentsEarned?: number;
  deckId?: number | null;
  // ЛОГ.1: текстовый лог боя для дебага. Сервер сохраняет в
  // match_history.log_text. Опционально.
  logEntries?: string[];
  // РЕП.1: Colyseus roomId. Сервер по нему берёт replay из кеша и
  // кладёт в match_history.replay_json.
  pvpRoomId?: string;
}): Promise<MatchFinishResult> {
  return request('/api/matches/finish', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

// ЛОГ.1.1: отправить «черновик» лога незавершённого матча для дебага.
// Создаёт запись в match_history с mode='abandoned', result='draw',
// log_text заполнен. БП-XP / лиги / награды НЕ начисляются.
//
// Используется при unmount BattleScreen если матч не закончился
// (юзер закрыл приложение / краш UI / отказ от боя).
export function abandonMatchLog(data: {
  faction?: string;
  heroId?: string;
  durationSec?: number;
  deckId?: number | null;
  logEntries: string[];
  reason?: string; // 'unmount' | 'crash' | 'manual_leave' | ...
}): Promise<{ ok: true; match_id: number }> {
  return request('/api/matches/abandon-log', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

// ============================================================
// МАР: маркетплейс карт (Этап 56)
// ============================================================
export interface MarketListingDto {
  id: number;
  sellerId: number;
  cardId: string;
  cardFaction: 'deus' | 'syntetics' | 'global' | 'livlife';
  cardRarity: 'common' | 'rare' | 'epic' | 'legendary';
  type: 'fix' | 'auction';
  currency: 'scripts' | 'usdt';
  fixPrice: number | null;
  startBid: number | null;
  currentBid: number | null;
  currentBidderId: number | null;
  durationHours: number | null;
  expiresAt: string | null;
  status: 'active' | 'sold' | 'expired' | 'cancelled';
  buyerId: number | null;
  soldPrice: number | null;
  commission: number | null;
  createdAt: string;
  soldAt: string | null;
}

export interface MarketBidDto {
  id: number;
  bidderId: number;
  bidderNick: string;
  amount: number;
  status: 'placed' | 'outbid' | 'won' | 'refunded';
  createdAt: string;
}

export interface MarketSellableItem {
  cardId: string;
  count: number;
  listedCount: number;
  available: number;
  faction: 'deus' | 'syntetics' | 'global' | 'livlife';
  rarity: string;
  name_ru: string;
  minKeep: number;
  sellableCount: number;  // сколько ещё можно выставить с учётом min-keep
}

export interface MarketConfig {
  commissionRate: number;
  minPriceScripts: number;
  minPriceUsdtCents: number;
  auctionBidStepPercent: number;
  auctionBidStepMinScripts: number;
  auctionBidStepMinUsdtCents: number;
  maxActiveListingsPerPlayer: number;
  auctionDurations: number[];           // [4, 24, 48]
  minKeepByRarity: Record<string, number>;
}

export interface MarketPool {
  season: { code: string; name: string; endsAt: string } | null;
  pool: {
    scriptsAccumulated: number;
    usdtPurchasesCents: number;
    distributedAt: string | null;
  };
  my: {
    spentCents: number;
    sharePercent: number;
    estimatedReward: number;
  };
}

export interface MarketListingsQuery {
  faction?: 'deus' | 'syntetics' | 'global' | 'livlife';
  rarity?: 'common' | 'rare' | 'epic' | 'legendary';
  currency?: 'scripts' | 'usdt';
  type?: 'fix' | 'auction';
  sort?: 'price_asc' | 'price_desc' | 'new' | 'ending_soon';
  limit?: number;
  offset?: number;
}

export function getMarketListings(q: MarketListingsQuery = {}): Promise<{ listings: MarketListingDto[] }> {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== null) usp.set(k, String(v));
  }
  const qs = usp.toString();
  return request(`/api/marketplace/listings${qs ? '?' + qs : ''}`);
}

export function getMarketListing(id: number): Promise<{ listing: MarketListingDto; bids: MarketBidDto[] }> {
  return request(`/api/marketplace/listings/${id}`);
}

export function createMarketListing(data: {
  card_id: string;
  type: 'fix' | 'auction';
  currency: 'scripts' | 'usdt';
  fix_price?: number;
  start_bid?: number;
  duration_hours?: 4 | 24 | 48;
}): Promise<{ ok: true; listing_id: number }> {
  return request('/api/marketplace/list', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export function cancelMarketListing(id: number): Promise<{ ok: true }> {
  return request(`/api/marketplace/cancel/${id}`, { method: 'POST' });
}

export function buyMarketListing(id: number): Promise<{ ok: true; sold_price: number; commission: number }> {
  return request(`/api/marketplace/buy/${id}`, { method: 'POST' });
}

export function placeMarketBid(id: number, amount: number): Promise<{ ok: true; new_bid: number }> {
  return request(`/api/marketplace/bid/${id}`, {
    method: 'POST',
    body: JSON.stringify({ amount })
  });
}

export function getMyMarketListings(scope: 'active' | 'sold' | 'bought' | 'bids'): Promise<{ listings: MarketListingDto[] }> {
  return request(`/api/marketplace/my/${scope}`);
}

export function getMarketPool(): Promise<MarketPool> {
  return request('/api/marketplace/pool');
}

export function getMarketSellable(): Promise<{ items: MarketSellableItem[] }> {
  return request('/api/marketplace/sellable');
}

export function getMarketConfig(): Promise<MarketConfig> {
  return request('/api/marketplace/config');
}
